<template>
  <transition
    appear
    :enterActiveClass="$style.fadeEnterActive"
    name="fade"
  >
    <ModalStructure
      :class="$style.modalStyles"
      data-test-id="membership-modal"
      :hasBlur="false"
      :shouldCloseOnBlur="false"
      :showCloseButton="true"
      :showModal="true"
      @close="handleClose"
    >
      <template v-slot:header>
        <h1 :class="$style.headerText">
          {{ headerText }}
        </h1>
      </template>
      <template v-slot:body>
        <div :class="[$style.bodyContainer, $style.large]">
          <div
            :class="[
              $style.contentSelectionActive,
              $style.contentFadeTransition,
              {[$style.invisible]: noSuggestionsState}
            ]"
          >
            <div :class="$style.contentContainer">
              <p :class="[$style.contentFadeTransition, {[$style.invisible]: fetchingSuggestions}]">
                Select users to confirm the suggested addition or removal from this group.
                Unselected users will not be updated.
              </p>
              <ItemTable
                ref="itemTable"
                v-bind="itemTableProps"
                data-test-id="MigratedMembershipSuggestionsModal__modalItemTable"
                :itemType="itemType"
                :onMountedAfterFetch="onMountedAfterFetch"
                :onMountedBeforeFetch="onMountedBeforeFetch"
              />
            </div>
          </div>
          <NoSuggestionsModal
            :class="[
              $style.contentSelectionActive,
              $style.contentFadeTransition,
              {[$style.invisible]: !noSuggestionsState}
            ]"
            data-test-id="no-suggestions-modal"
            :includeButtons="false"
            :itemName="itemName"
            :svgSize="190"
          />
        </div>
      </template>
      <template v-slot:footer>
        <div :class="$style.footerContainer">
          <div
            :class="[
              $style.footerLabels,
              $style.contentFadeTransition,
              {[$style.invisible]: !hasMembershipSuggestions}
            ]"
          >
            <div :class="$style.actionContainer">
              <span :class="$style.actionLabel">Adding</span>
              <span>
                <span :class="$style.counter">{{ addCounter }}</span>
                {{ addLabelUsersText }}
              </span>
            </div>
            <div :class="$style.actionContainer">
              <span :class="$style.actionLabel">Removing</span>
              <span>
                <span :class="$style.counter">{{ removeCounter }}</span>
                {{ removeLabelUsersText }}
              </span>
            </div>
          </div>
          <ButtonGroup
            :class="$style.buttonGroup"
            :isFullWidth="false"
          >
            <Button
              :class="[$style.contentFadeTransition, {[$style.invisible]: noSuggestionsState}]"
              data-test-id="cancel-button"
              hasOutline
              text="Close"
              type="secondary"
              @click="handleClose"
            />
            <Button
              :class="$style.continueButton"
              data-test-id="membership-modal-saveButton"
              :isDisabled="!noSuggestionsState && !enableMainAction"
              :isLoading="fetchingSuggestions"
              :text="continueButtonProps.text"
              textCase="inherit"
              type="primary"
              @click="continueButtonProps.clickAction"
            />
          </ButtonGroup>
        </div>
      </template>
    </ModalStructure>
  </transition>
</template>

<script>
import {
  Button, ButtonGroup, ModalStructure,
} from '@jumpcloud/ui-components';
import { cloneDeep } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import pluralize from 'pluralize';

import { ItemTypeConsts, StoreModelMap, StoreModuleMap } from 'client/util/Constants';
import { gradientLight2 } from 'client/css/colors.css';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import ItemTable from 'client/components/ItemTable/ItemTable.vue';
import ItemTableMethodsMixin from 'client/components/ItemTable/mixins/ItemTableMethodsMixin';
import ItemTablePropsMixin from 'client/components/ItemTable/mixins/ItemTablePropsMixin';
import NoSuggestionsModal from 'client/groups/User/NoSuggestionsModal.vue';
import SystemUserItemTableColumns from 'client/systemusers/SystemUserItemTable/SystemUserItemTableColumns';
import Util from 'client/util/Util';

const suggestedSystemUsersCollection = StoreModuleMap[ItemTypeConsts.suggestedSystemUser];
const userGroupModel = StoreModelMap.user_group;

export default {
  name: 'MembershipSuggestionsModal',

  components: {
    Button,
    ButtonGroup,
    ItemTable,
    ModalStructure,
    NoSuggestionsModal,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
    ItemTableMethodsMixin,
    ItemTablePropsMixin,
  ],

  props: {
    handleCancel: {
      type: Function,
      default: () => {},
    },
    handleContinue: {
      type: Function,
      default: () => {},
    },
    itemId: {
      type: String,
      default: undefined,
    },
    itemName: {
      type: String,
      default: undefined,
    },
    itemType: {
      type: String,
      required: true,
      default: ItemTypeConsts.suggestedSystemUser,
    },
    redirectAfterClosing: {
      default: false,
      type: Boolean,
    },
    review: {
      default: false,
      type: Boolean,
    },
  },

  data() {
    return {
      addCounter: 0,
      addSystemUserIds: [],
      enableMainAction: false,
      gradientLight2,
      fetchingSuggestions: false,
      itemTableProps: {
        columns: [
          {
            ...SystemUserItemTableColumns.suggestion,
            classNames: {
              body: [this.$style.suggestionsFieldColumn],
              heading: [this.$style.suggestionsColumn],
            },
            headingText: 'Suggestion',
            sortBy: undefined,
          },
          {
            ...SystemUserItemTableColumns.name,
            classNames: {
              body: [this.$style.suggestionsNameColumn],
              heading: [this.$style.suggestionsColumn],
            },
            sortBy: undefined,
          },
        ],
        legacyRowStyles: false,
        details: false,
        inPanel: false,
        hasSelectColumn: true,
        hideSelectionTotalCount: true,
        itemType: ItemTypeConsts.suggestedSystemUser,
        loadingOverlayText: 'Looking for group membership suggestions...',
        showActionBar: false,
        showBound: false,
        showBulkAction: false,
        showSearchForm: false,
        useLargeTableCells: true,
      },
      loading: false,
      removeCounter: 0,
      removeSystemUserIds: [],
    };
  },

  computed: {
    addLabelUsersText() {
      return pluralize('user', this.addCounter, false);
    },

    continueButtonProps() {
      return {
        text: !this.noSuggestionsState
          ? 'Accept and Save'
          : 'Got it',
        clickAction: !this.noSuggestionsState
          ? this.handleMainAction
          : this.handleClose,
      };
    },

    hasMembershipSuggestions() {
      return Number(this.suggestionsTotalCount) > 0;
    },

    headerText() {
      if (this.fetchingSuggestions) {
        return 'Fetching membership suggestions...';
      }

      if (this.noSuggestionsState) {
        return 'No membership suggestions found!';
      }

      let membershipSuggestionsText = 'Membership suggestions';

      if (this.displayName) {
        membershipSuggestionsText += ` for ${this.displayName}`;
      } else if (this.itemName) {
        membershipSuggestionsText += ` for ${this.itemName}`;
      }

      return membershipSuggestionsText;
    },

    /**
     * When no suggestions have been found by ItemTable.
     */
    noSuggestionsState() {
      return !this.fetchingSuggestions && !this.hasMembershipSuggestions;
    },

    removeLabelUsersText() {
      return pluralize('user', this.removeCounter, false);
    },
  },

  watch: {
    async getSelectedSystemUsers(newUsers) {
      if (newUsers.length === 0) {
        this.addCounter = 0;
        this.removeCounter = 0;
      } else {
        const { allSuggestions } = await this.getSuggestions();

        this.addSystemUserIds = newUsers.filter(id => allSuggestions.find(item => item.object.id === id && item.op === 'add'));
        this.removeSystemUserIds = newUsers.filter(id => allSuggestions.find(item => item.object.id === id && item.op === 'remove'));

        this.addCounter = this.addSystemUserIds.length;
        this.removeCounter = this.removeSystemUserIds.length;
      }
    },

    selectedItemsCount(newCount) {
      this.enableMainAction = newCount > 0;
    },
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options);

    this.$options.computed = {
      ...this.$options.computed,

      ...mapGetters(suggestedSystemUsersCollection, {
        getSelectedSystemUsers: 'getSelectedItems',
        selectedItemsCount: 'getSelectedItemsCount',
      }),

      ...mapState(suggestedSystemUsersCollection, {
        suggestionsTotalCount: state => state.entities.items.totalCountOfAllItems,
      }),

      ...mapState(userGroupModel, {
        displayName: state => state.item.name,
        rules: state => state.item.memberQuery,
      }),
    };

    this.$options.methods = {
      ...this.$options.methods,

      ...mapActions(userGroupModel, {
        fetchUserGroupItem: 'fetchUserGroupItem',
      }),
    };
  },

  async beforeMount() {
    let conditions = this.rules;

    if (this.review) {
      const id = this.itemId;
      const item = await this.fetchUserGroupItem({ id });
      const { memberQuery } = item;
      conditions = memberQuery;
    }

    if (conditions && conditions.filters) {
      const rules = cloneDeep(conditions.filters);
      rules.sort((a, b) => a.field.localeCompare(b.field));

      rules.forEach((rule) => {
        const newColumn = {
          ...SystemUserItemTableColumns[rule.field],
          classNames: {
            body: [this.$style.suggestionsFieldColumn],
            heading: [this.$style.suggestionsColumn],
          },
          sortBy: undefined,
        };

        this.itemTableProps.columns.push(newColumn);
      });
    }
  },

  methods: {
    ...mapActions(suggestedSystemUsersCollection, {
      acceptSuggestions: 'acceptSuggestions',
      cleanSuggestions: 'cleanSuggestions',
      getSuggestions: 'getSuggestions',
      resetCollection: 'resetCollection',
    }),

    handleClose() {
      const payload = {
        action: 'Close Membership Suggestions Modal',
        type: 'Membership Suggestions',
      };

      EventHelper.triggerMembershipSuggestionsModalClosed(payload);

      this.loading = false;
      this.cleanSuggestions();
      this.$emit('close');
      if (this.redirectAfterClosing) Util.redirect(`#/groups/user/${this.itemId}/details/users`);
    },

    async handleMainAction() {
      if (this.addCounter >= 100 || this.removeCounter >= 100) {
        this.$emit('close');
      }

      const id = this.itemId;

      const payload = {
        user_ids: [...this.addSystemUserIds, ...this.removeSystemUserIds],
      };

      await this.acceptSuggestions({ id, payload });

      await this.handleContinue({
        ids: this.removeSystemUserIds,
        usersAdded: this.addCounter,
        usersRemoved: this.removeCounter,
      });

      this.handleClose();
    },

    onMountedAfterFetch() {
      if (!this.hasMembershipSuggestions) {
        this.cleanSuggestions();
      }

      // Done fetching, disable "loading" state.
      this.fetchingSuggestions = false;
    },

    onMountedBeforeFetch() {
      /**
       * We can know when the ItemTable is searching for suggestions
       * by hooking into its onMountedBeforeFetch & onMountedAfterFetch
       * callbacks.
       *
       * Start fetching, enable "loading" state.
       */
      this.fetchingSuggestions = true;

      this.cleanSuggestions();
      this.resetCollection();
    },
  },
};
</script>

<style module>
@value ( jcPrimary50 ) from '~css/brand-colors.css';
@value (panelSpeedSm,panelTimingFunction) from '~css/transitions.css';

.modalStyles {
  --modal-width: 65%;
}

.actionContainer {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  min-width: 100px;
  padding-right: 25px;
}

.actionLabel {
  color: var(--jc-color-neutral-text-subdued);
}

.bodyContainer {
  height: 400px;
  position: relative;
  width: 100%;
}

/*
  When using ButtonGroup and unable to get the Buttons
  to display side-by-side, use "flex-basis: 100%;"
*/
.buttonGroup {
  flex-basis: 100%;
  user-select: none;
}

.content {
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  transition: transform 0.5s ease-in-out;
  width: 100%;
}

.contentContainer {
  height: 94%;
}

.contentFadeTransition {
  transition: opacity 0.5s ease-out;
}

.contentSelection {
  composes: content;
  transform: translateX(-150%);
}

.contentSelectionActive {
  composes: content;
  transform: translateX(0);
}

.contentResults {
  composes: content;
  transform: translateX(150%);
}

.continueButton {
  transition: opacity 0.5s ease-out;
}

.counter {
  font-size: 18px;
  font-weight: bold;
}

.footerContainer {
  align-content: center;
  display: flex;
  width: 100%;
}

.footerLabels {
  display: flex;
  flex-direction: row;
}

/*
   In order to apply desired style changes to itemTable component,
   an override is needed.
 */

/* stylelint-disable selector-descendant-combinator-no-non-space */
:global([data-test-id="MigratedMembershipSuggestionsModal__modalItemTable"]
div div .ItemTable__tableHeadCell) {
  background: var(--jc-color-surface-light);
}

:global([data-test-id="MigratedMembershipSuggestionsModal__modalItemTable"]
div div .ItemTable__tableBodyCell) {
  background-color: transparent;
}

:global([data-test-id="MigratedMembershipSuggestionsModal__modalItemTable"]
div div .ItemTable__tableBodyRowSelected) {
  background-color: var(--jcPrimary50);
  border-top: 1px solid var(--jc-border-color);
  box-shadow: none;
  transition-duration: panelSpeedSm;
  transition-property: background-color, box-shadow;
  transition-timing-function: panelTimingFunction;
}

:global([data-test-id="MigratedMembershipSuggestionsModal__modalItemTable"]
div div .ItemTable__tableBodyRow:hover) {
  background-color: var(--jcPrimary50);
  border-top: 1px solid var(--jc-border-color);
  box-shadow: none;
  transition-duration: panelSpeedSm;
  transition-property: background-color, box-shadow;
  transition-timing-function: panelTimingFunction;
}

:global([data-test-id="MigratedMembershipSuggestionsModal__modalItemTable"]
div div
.ItemTable__tableHeadCell.ItemTable__largeTableCellHeader.ItemTable__selectColumn label) {
  background: var(--jc-color-surface-light);
}

.headerText {
  margin-bottom: 0;
  overflow: hidden;
}

.large {
  height: 600px;
}

.invisible {
  opacity: 0;
  pointer-events: none;
}

.suggestionsColumn.suggestionsColumn {
  font-size: 1rem;
  padding: 15px 7px 10px 6px;
  text-align: left;
}

.suggestionsFieldColumn.suggestionsFieldColumn {
  font-size: 1rem;
  padding: 22px 8px 22px 7px;
  text-align: left;
}

.suggestionsNameColumn.suggestionsNameColumn {
  padding: 10px 7px 10px 6px;
  text-align: left;
}

/*
  When ItemTable opens, it takes a split second to load the suggestions, which results
  in an abrupt state change from loading suggestions -> displaying suggestions within
  the first second of loading.

  As such, here we employ a brief fade-in transition so the user "misses"
  this abrupt state change and is just greeted with the loaded ItemTable.
*/
.fadeEnterActive {
  animation: fadeIn 0.6s ease-out;
}

@keyframes fadeIn {
  0%,
  40% { opacity: 0; }

  100% { opacity: 1; }
}
</style>
